/**
 * React Query Provider
 * Configurazione QueryClient con default options e gestione errori globali
 */

import { QueryClient, QueryClientProvider, MutationCache, QueryCache } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { toast } from '@/lib/toast-service';
import { ApiClientError } from '@/lib/api-client';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minuti per dati che cambiano raramente
      gcTime: 10 * 60 * 1000, // 10 minuti (precedentemente cacheTime)
      retry: 1, // 1 tentativo per errori di rete
      refetchOnWindowFocus: false, // Evita chiamate eccessive
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 0, // Non ritentare le mutazioni
    },
  },
  mutationCache: new MutationCache({
    onError: (error) => {
      // Mostra toast automatico solo per errori API
      // Ignora errori di validazione locale o altri errori non-API
      if (error instanceof ApiClientError || error instanceof Error) {
        // Mostra toast solo se non è già gestito esplicitamente nella mutation
        // (le mutations possono avere il proprio onError che gestisce il toast)
        toast.error(error);
      }
    },
  }),
  queryCache: new QueryCache({
    // Per le query, non mostriamo toast automatici
    // Gli errori di query sono spesso gestiti meglio con stati di loading/error nei componenti
    // Le mutations hanno già gestione automatica degli errori tramite mutationCache
  }),
});

interface QueryProviderProps {
  children: ReactNode;
}

export function QueryProvider({ children }: QueryProviderProps) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
}

